/**
 * Token trigger utilities for the composer.
 *
 * Three trigger types:
 *   mention:  `@[Agent Name]`            — bracket-wrapped so the friendly name
 *                                          (with spaces) round-trips; the agent
 *                                          ids are sent out-of-band as `mention_ids`
 *   resource: `#[Resource Name]`         — bracket-wrapped so spaces round-trip
 *   command:  `/command`                 — no brackets; the command word is
 *                                          the trigger + alphanumerics until
 *                                          whitespace or end of line
 */

type TokenTrigger = '@' | '#' | '/'

export interface ActiveToken {
  trigger: TokenTrigger
  query: string
  /** Character offset of the trigger character itself. */
  start: number
}

/**
 * Find the in-progress mention/resource/command token at the caret, if any.
 * A trigger only counts when it starts the string or follows whitespace, so
 * emails (`foo@bar`) and `#` mid-word don't open the menu.
 * `@` mentions and `/` commands are single-word (no spaces); `#` resources
 * allow spaces in the query.
 */
export function detectActiveToken(
  text: string,
  caret: number,
): ActiveToken | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i]
    // Brackets and newlines terminate any token scan.
    if (ch === '\n' || ch === '[' || ch === ']') return null
    if (ch === '@' || ch === '#' || ch === '/') {
      const prev = i > 0 ? text[i - 1] : ''
      if (i !== 0 && !/\s/.test(prev)) return null
      const query = text.slice(i + 1, caret)
      // `@` mentions and `/` commands are single-word identifiers.
      if ((ch === '@' || ch === '/') && !/^[\w-]*$/.test(query)) return null
      return { trigger: ch, query, start: i }
    }
  }
  return null
}

/**
 * Replace the token range [start, caret) in `text` with `insert`, appending a
 * trailing space, and return the new caret position (just past the space).
 */
export function replaceToken(
  text: string,
  start: number,
  caret: number,
  insert: string,
): { text: string; caret: number } {
  const before = text.slice(0, start)
  const after = text.slice(caret)
  return {
    text: `${before}${insert} ${after}`,
    caret: before.length + insert.length + 1,
  }
}

// ---------------------------------------------------------------------------
// Mentions
// ---------------------------------------------------------------------------

export function formatMention(name: string): string {
  return `@[${name}]`
}

/** A mention picked from the `@` autocomplete, captured at selection time.
 * Agents carry their agent id; users carry their username. */
export interface MentionRef {
  id: string
  name: string
  kind: 'agent' | 'user'
}

/**
 * Given the final message text and the refs inserted during composition,
 * return the refs whose `@[name]` token still appears in the text (so
 * mentions the user deleted are dropped), de-duplicated by id, in insertion
 * order. Callers split by `kind`: agent ids feed `mention_ids`, usernames
 * feed `mentioned_usernames`.
 */
export function reconcileMentionRefs(
  text: string,
  refs: MentionRef[],
): MentionRef[] {
  const matched: MentionRef[] = []
  const seen = new Set<string>()
  for (const r of refs) {
    if (seen.has(r.id)) continue
    if (text.includes(formatMention(r.name))) {
      matched.push(r)
      seen.add(r.id)
    }
  }
  return matched
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

export function formatResource(name: string): string {
  return `#[${name}]`
}

/** A resource picked from the `#` autocomplete, captured at selection time. */
export interface ResourceRef {
  id: string
  name: string
  kind: string
}

/**
 * Given the final message text and the refs inserted during composition,
 * return the ids of refs whose `#[name]` token still appears in the text
 * (so refs the user deleted are dropped), de-duplicated by id.
 */
export function reconcileResourceRefs(
  text: string,
  refs: ResourceRef[],
): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  for (const r of refs) {
    if (seen.has(r.id)) continue
    if (text.includes(formatResource(r.name))) {
      ids.push(r.id)
      seen.add(r.id)
    }
  }
  return ids
}

// ---------------------------------------------------------------------------
// Slash commands
// ---------------------------------------------------------------------------

export interface SlashCommand {
  /** The command name without the leading `/`. */
  name: string
  /** Short description shown in the autocomplete menu. */
  description: string
  /** Optional usage hint, e.g. `<query>` or `<topic-id>`. */
  usage?: string
}

/**
 * Built-in slash commands available in the composer.
 * These are metadata for autocomplete — the backend interprets them.
 */
export const SLASH_COMMANDS: SlashCommand[] = [
  { name: 'summarize', description: 'Summarize the conversation so far' },
  { name: 'compact', description: 'Force a context compaction' },
  {
    name: 'title',
    description: 'Generate a new title for this thread',
    usage: '<optional title>',
  },
  { name: 'help', description: 'Show available commands and usage' },
]

/**
 * Filter slash commands by the query string (prefix match, case-insensitive).
 */
export function filterSlashCommands(query: string): SlashCommand[] {
  const q = query.toLowerCase()
  return SLASH_COMMANDS.filter((c) => c.name.toLowerCase().startsWith(q))
}
